import { initContract } from '@ts-rest/core'
import z from 'zod'
import { ErrorResponseSchema } from '../../shared/types'
import {
  AddOrderSubscriptionExtraInputSchema,
  AddressSchema,
  BackfillOutboxBodySchema,
  BackfillOutboxResponseSchema,
  BackfillProductTypeInputSchema,
  BackfillProductTypeOutputSchema,
  BatchUpdateOrdersSchema,
  ClaimOutboxBatchBodySchema,
  ClaimOutboxBatchResponseSchema,
  CreateOrderComplaintInputSchema,
  UpdateOrderComplaintInputSchema,
  CreateOrderDiscountBodySchema,
  CreateOrderDiscountResponseSchema,
  CreateOrderEventSchema,
  CreateOrderReferrerRewardBodySchema,
  CreateOrderReferrerRewardResponseSchema,
  CustomerFlagsResponseSchema,
  DeleteMigratedOrderDiscountsResponseSchema,
  DeleteOrderSubscriptionExtraInputSchema,
  ForecastBoxesAtFacilityPayloadSchema,
  FutureBoxesByDateSchema,
  FutureUnitsByDateInputSchema,
  FutureUnitsByDateSchema,
  GetDeliveryPromotionCandidatesQuerySchema,
  GetDeliveryPromotionCandidatesResponseSchema,
  GetNetsuiteInternalIdQuerySchema,
  GetOrderComplaintsQuerySchema,
  GetOrderComplaintsResponseSchema,
  GetOrderReferrerRewardsQuerySchema,
  GetOrderReferrerRewardsResponseSchema,
  GetOrdersResponseSchema,
  GetPhoenixToggleOrdersQuerySchema,
  GetPhoenixToggleOrdersResponseSchema,
  GetOrdersForPricingBackfillQuerySchema,
  GetOrdersForPricingBackfillResponseSchema,
  GetOrdersForSubscriptionHistoryBackfillQuerySchema,
  GetOrdersForSubscriptionHistoryBackfillResponseSchema,
  BatchSetSubscriptionHistoryIdBodySchema,
  BatchSetSubscriptionHistoryIdResponseSchema,
  FreezePaidOrdersToSnapshotBodySchema,
  FreezePaidOrdersToSnapshotResponseSchema,
  GetOrdersForMigratedConsolidationQuerySchema,
  GetOrdersForMigratedConsolidationResponseSchema,
  ConsolidateMigratedOrdersBodySchema,
  ConsolidateMigratedOrdersResponseSchema,
  GetPhoenixToggleCustomerIdsQuerySchema,
  GetPhoenixToggleCustomerIdsResponseSchema,
  RepointOrdersToActiveSubscriptionBodySchema,
  RepointOrdersToActiveSubscriptionResponseSchema,
  GetOrdersForCutoffBackfillQuerySchema,
  GetOrdersForCutoffBackfillResponseSchema,
  GetOrdersForSnapshotBackfillQuerySchema,
  GetOrdersForSnapshotBackfillResponseSchema,
  GetSlimOrdersForBulkOperationQuerySchema,
  GetSlimOrdersForBulkOperationResponseSchema,
  GetOrderByIdQuerySchema,
  GetUpcomingOrdersForProductionDateAlertQuerySchema,
  GetUpcomingOrdersForProductionDateAlertResponseSchema,
  ListNetsuiteInternalIdsResponseSchema,
  MarkOutboxFailedBodySchema,
  MarkOutboxProcessedBodySchema,
  GetComplaintCategoriesResponseSchema,
  MigrateOrderComplaintSchema,
  MigrateOrderDiscountSchema,
  MoveOrderSubscriptionExtraInputSchema,
  NetsuiteInternalIdSchema,
  NetsuiteSyncSummaryQuerySchema,
  NetsuiteSyncSummaryResponseSchema,
  OrderComplaintSchema,
  OrderEventSchema,
  OrderFiltersSchema,
  OrderFlagsByLegacyIdResponseSchema,
  OrderFlagsInputSchema,
  OrderFulfilmentStatusSchema,
  OrderInputSchema,
  OrderLineItemSchema,
  OrderPaymentStatusSchema,
  OrderReferrerRewardRecordSchema,
  OrderResponseSchema,
  OrdersByLegacyIdsBodySchema,
  OrdersByLegacyIdsResponseSchema,
  OrderSchema,
  OutboxPredecessorSchema,
  OutboxSupersedingRowSchema,
  PatchOrderDiscountBodySchema,
  RebalanceOrdersForDateQuerySchema,
  RebalanceOrdersForDateResponseSchema,
  GetRebalanceMoveContextPayloadSchema,
  GetRebalanceMoveContextResponseSchema,
  RecordDiscoveredNetsuiteInternalIdBodySchema,
  ReplayOutboxRowBodySchema,
  ReplayOutboxRowResponseSchema,
  SearchOrdersQuerySchema,
  SearchOrdersResponseSchema,
  UpdateAddressSchema,
  UpdateOrderLineItemSchema,
  UpdateOrderReferrerRewardBodySchema,
  UpdateOrderSubscriptionExtraInputSchema,
  UpsertOrderCutoffsSchema,
  VerifyOrderDiscountResponseSchema,
  VerifyOrderResponseSchema,
  DeletePendingOrderByUserIdResponseSchema,
} from './schemas'
import { PaymentStatus } from './types'
import { OrderRebalancedPayload } from './worker-events'

const c = initContract()

const options = {
  pathPrefix: '/api/v1',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    409: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

export const orderServiceContract = c.router(
  {
    createOrder: {
      method: 'POST',
      path: '/orders',
      body: OrderInputSchema,
      responses: {
        200: OrderResponseSchema,
      },
    },

    getOrderReferrerRewards: {
      method: 'GET',
      description: 'List referrer reward records for a user',
      path: '/orders/referrer-rewards',
      query: GetOrderReferrerRewardsQuerySchema,
      responses: {
        200: GetOrderReferrerRewardsResponseSchema,
      },
    },

    getSlimOrdersForBulkOperation: {
      method: 'GET',
      path: '/orders/slim-for-bulk-operation',
      summary: 'Cursor-paginated slim order list filtered by timezone + local billing date, for bulk operations',
      query: GetSlimOrdersForBulkOperationQuerySchema,
      responses: {
        200: GetSlimOrdersForBulkOperationResponseSchema,
      },
    },

    getOrdersForPricingBackfill: {
      method: 'GET',
      path: '/orders/for-pricing-backfill',
      summary: 'Cursor-paginated order list for the pricing backfill (Tyga or Laravel-migrated cohort)',
      query: GetOrdersForPricingBackfillQuerySchema,
      responses: {
        200: GetOrdersForPricingBackfillResponseSchema,
      },
    },

    getOrdersForCutoffBackfill: {
      method: 'GET',
      path: '/orders/for-cutoff-backfill',
      summary: 'Cursor-paginated orders eligible for order_cutoffs backfill, ordered by delivery_date ASC (soonest first)',
      description: 'By default returns orders missing an order_cutoffs row. Pass includeNonProcessed=true to also include non-processed orders that already have a cache row (stale-cache re-resolve).',
      query: GetOrdersForCutoffBackfillQuerySchema,
      responses: {
        200: GetOrdersForCutoffBackfillResponseSchema,
      },
    },

    getOrdersForSnapshotBackfill: {
      method: 'GET',
      path: '/orders/for-snapshot-backfill',
      summary: 'Cursor-paginated paid Phoenix orders (not yet started) eligible for meal plan snapshot backfill, ordered by id ASC',
      description: 'Returns paid orders whose customer is Phoenix — either customer_flags.phoenix_toggle = true (migrated) OR genuine native (no customer_flags row AND no order_id_mapping row) — and whose fulfilment_status is still pre-production (pending/deprioritised/backlog/on_hold). The order_id_mapping guard excludes legacy un-migrated customers, whose CDC-mirrored orders carry a legacy mapping but no flags row. Deliberately does NOT filter on snapshot presence — the refresh is idempotent and also repairs snapshots with empty recipes. The fulfilment-side started guard remains the source of truth downstream.',
      query: GetOrdersForSnapshotBackfillQuerySchema,
      responses: {
        200: GetOrdersForSnapshotBackfillResponseSchema,
      },
    },

    getOrdersForSubscriptionHistoryBackfill: {
      method: 'GET',
      path: '/orders/for-subscription-history-backfill',
      summary: 'Cursor-paginated subscription orders (with subscription_id, without subscription_history_id) for the subscription-history backfill',
      query: GetOrdersForSubscriptionHistoryBackfillQuerySchema,
      responses: {
        200: GetOrdersForSubscriptionHistoryBackfillResponseSchema,
      },
    },

    batchSetSubscriptionHistoryId: {
      method: 'PATCH',
      path: '/orders/backfill/subscription-history',
      summary: 'Bulk-set subscription_history_id on orders (backfill only — does not touch subscription_id or line items)',
      body: BatchSetSubscriptionHistoryIdBodySchema,
      responses: {
        200: BatchSetSubscriptionHistoryIdResponseSchema,
      },
    },

    freezePaidOrdersToSnapshot: {
      method: 'PATCH',
      path: '/orders/freeze-paid-orders-snapshot',
      summary: 'Point every PROCESSED order still tracking the live subscription (subscription_history_id = subscription_id) at a subscriptions_history snapshot, after a plan change',
      body: FreezePaidOrdersToSnapshotBodySchema,
      responses: {
        200: FreezePaidOrdersToSnapshotResponseSchema,
      },
    },

    getOrdersForMigratedConsolidation: {
      method: 'GET',
      path: '/orders/for-migrated-consolidation',
      summary: 'Cursor-paginated migrated (order_id_mapping) Lyka orders on phoenix-toggle customers not yet consolidated (subscription_history_id IS NULL)',
      query: GetOrdersForMigratedConsolidationQuerySchema,
      responses: {
        200: GetOrdersForMigratedConsolidationResponseSchema,
      },
    },

    consolidateMigratedOrders: {
      method: 'PATCH',
      path: '/orders/backfill/consolidate-migrated-subscription',
      summary: 'Port migrated orders\' subscription_id → subscription_history_id, move them onto the live subscription, and re-sequence subscription_order_index for that subscription',
      body: ConsolidateMigratedOrdersBodySchema,
      responses: {
        200: ConsolidateMigratedOrdersResponseSchema,
      },
    },

    getPhoenixToggleCustomerIds: {
      method: 'GET',
      path: '/orders/customers/phoenix-toggle-ids',
      summary: 'Cursor-paginated distinct customer ids with phoenix_toggle = true (each customer returned once, ascending by id)',
      query: GetPhoenixToggleCustomerIdsQuerySchema,
      responses: {
        200: GetPhoenixToggleCustomerIdsResponseSchema,
      },
    },

    repointOrdersToActiveSubscription: {
      method: 'PATCH',
      path: '/orders/backfill/repoint-active-subscription',
      summary: 'Move ALL of a phoenix-toggle customer\'s Lyka orders onto their active subscription and re-sequence subscription_order_index (leaves subscription_history_id untouched)',
      body: RepointOrdersToActiveSubscriptionBodySchema,
      responses: {
        200: RepointOrdersToActiveSubscriptionResponseSchema,
      },
    },

    getDeliveryPromotionCandidates: {
      method: 'GET',
      path: '/orders/delivery-promotion-candidates',
      summary: 'Cursor-paginated palletised + phoenix-toggle orders whose local delivery day equals the given date in the given timezone, for the delivered-promotion scheduler',
      query: GetDeliveryPromotionCandidatesQuerySchema,
      responses: {
        200: GetDeliveryPromotionCandidatesResponseSchema,
      },
    },

    searchOrders: {
      method: 'GET',
      path: '/orders/search',
      summary: 'Prefix search orders by receipt number or UUID',
      description: 'Used by CCA global search. Full UUID → PK lookup; digit prefix → order_number::text LIKE prefix; otherwise 400.',
      query: SearchOrdersQuerySchema,
      responses: {
        200: SearchOrdersResponseSchema,
      },
    },

    getOrderById: {
      method: 'GET',
      path: '/orders/:orderId',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      query: GetOrderByIdQuerySchema,
      responses: {
        200: OrderResponseSchema,
      },
    },

    getOrders: {
      method: 'GET',
      path: '/orders',
      query: OrderFiltersSchema,
      responses: {
        200: GetOrdersResponseSchema,
      },
    },

    updateOrder: {
      method: 'PUT',
      deprecated: true,
      description: 'Deprecated: Use `patchOrder` instead. PUT is used for overriding a resource',
      path: '/orders/:orderId',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: OrderSchema,
      responses: {
        200: OrderResponseSchema,
      },
    },

    batchPatchOrder: {
      method: 'PATCH',
      path: '/orders/batch',
      body: BatchUpdateOrdersSchema,
      responses: {
        200: OrderResponseSchema.array(),
      },
    },

    patchOrder: {
      method: 'PATCH',
      path: '/orders/:orderId',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: OrderSchema,
      responses: {
        200: OrderResponseSchema,
      },
    },

    patchOrderDiscount: {
      method: 'PATCH',
      description: 'Atomically remove/apply an order discount and patch the order\'s totals in one DB transaction',
      path: '/orders/:orderId/discount',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: PatchOrderDiscountBodySchema,
      responses: {
        200: OrderResponseSchema,
      },
    },

    getOrderEvents: {
      method: 'GET',
      path: '/orders/:orderId/events',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      responses: {
        200: OrderEventSchema.array(),
      },
    },

    createOrderEvent: {
      method: 'POST',
      path: '/orders/:orderId/events',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: CreateOrderEventSchema,
      responses: {
        200: OrderEventSchema,
      },
    },

    updateOrderPaymentStatus: {
      method: 'PATCH',
      path: '/orders/:orderId/payment-status',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: OrderPaymentStatusSchema,
      responses: {
        204: null,
      },
    },

    updateOrderFulfilmentStatus: {
      method: 'PATCH',
      path: '/orders/:orderId/fulfilment-status',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: OrderFulfilmentStatusSchema,
      responses: {
        204: null,
      },
    },

    updateOrderAddress: {
      method: 'PUT',
      path: '/orders/:orderId/addresses/:addressId',
      pathParams: z.object({
        orderId: z.string().uuid(),
        addressId: z.string().uuid(),
      }),
      body: UpdateAddressSchema,
      responses: {
        200: AddressSchema,
      },
    },

    upsertOrderLineItems: {
      method: 'PUT',
      path: '/orders/:orderId/line-items',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: UpdateOrderLineItemSchema.array(),
      responses: {
        200: OrderLineItemSchema.array(),
      },
    },

    updateOrderLineItem: {
      method: 'PUT',
      path: '/orders/:orderId/line-items/:lineItemId',
      pathParams: z.object({
        orderId: z.string().uuid(),
        lineItemId: z.string().uuid(),
      }),
      body: UpdateOrderLineItemSchema,
      responses: {
        200: OrderLineItemSchema,
      },
    },
    deleteOrdersForSubscriptionByPaymentStatus: {
      method: 'DELETE',
      path: '/orders/subscriptions/:subscriptionId',
      description: 'Delete orders for a subscription by payment status.',
      pathParams: z.object({
        subscriptionId: z.string().uuid(),
      }),
      query: z.object({
        // Only allow deleting pending order for now
        paymentStatus: z.enum([PaymentStatus.PENDING]),
      }),
      responses: {
        200: z.object({
          deletedOrderIds: z.string().uuid().array(),
        }),
      },
    },
    deleteOrder: {
      method: 'DELETE',
      path: '/orders/:orderId',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      responses: {
        204: null,
      },
    },

    deleteOrderLineItem: {
      method: 'DELETE',
      path: '/orders/:orderId/line-items/:lineItemId',
      pathParams: z.object({
        orderId: z.string().uuid(),
        lineItemId: z.string().uuid(),
      }),
      responses: {
        204: null,
      },
    },

    forecastBoxesAtFacility: {
      method: 'GET',
      path: '/forecast/boxes/facility/:facilityId',
      pathParams: z.object({
        facilityId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      query: ForecastBoxesAtFacilityPayloadSchema,
      responses: {
        200: FutureBoxesByDateSchema,
      },
    },

    getRebalanceOrdersForDate: {
      method: 'GET',
      path: '/forecast/rebalance/facility/:facilityId/orders',
      pathParams: z.object({
        facilityId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      query: RebalanceOrdersForDateQuerySchema,
      responses: {
        200: RebalanceOrdersForDateResponseSchema,
      },
    },

    getRebalanceMoveContext: {
      method: 'POST',
      path: '/forecast/rebalance/facility/:facilityId/orders/move-context',
      pathParams: z.object({
        facilityId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      body: GetRebalanceMoveContextPayloadSchema,
      responses: {
        200: GetRebalanceMoveContextResponseSchema,
      },
    },

    updateOrderMakeDate: {
      method: 'PUT',
      path: '/orders/:orderId/rebalance',
      body: OrderRebalancedPayload,
      responses: {
        204: null,
      },
    },

    upsertOrderCutoffs: {
      method: 'PUT',
      path: '/orders/:orderId/cutoffs',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: UpsertOrderCutoffsSchema,
      responses: {
        204: null,
      },
    },

    resolveOrderCutoffs: {
      method: 'POST',
      path: '/orders/:orderId/cutoffs/resolve',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: z.object({}).optional(),
      responses: {
        204: null,
      },
    },

    getStockMonitorOrderProductsAtFacility: {
      method: 'GET',
      path: '/stock-monitor/products/facility/:facilityId',
      pathParams: z.object({
        facilityId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      query: FutureUnitsByDateInputSchema,
      responses: {
        200: FutureUnitsByDateSchema,
      },
    },

    getOrderByLegacyOrderId: {
      method: 'GET',
      path: '/orders/lookup/by-legacy-id/:legacyOrderId',
      pathParams: z.object({
        legacyOrderId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      responses: {
        200: OrderResponseSchema,
      },
    },

    getOrdersByLegacyOrderIds: {
      method: 'POST',
      path: '/orders/lookup/by-legacy-ids',
      body: OrdersByLegacyIdsBodySchema,
      responses: {
        200: OrdersByLegacyIdsResponseSchema,
      },
    },

    getOrderFlagsByLegacyOrderId: {
      method: 'GET',
      path: '/orders/lookup/flags/by-legacy-id/:legacyOrderId',
      pathParams: z.object({
        legacyOrderId: z.string().transform(val => Number.parseInt(val, 10)),
      }),
      responses: {
        200: OrderFlagsByLegacyIdResponseSchema.nullable(),
      },
    },

    getCustomerFlags: {
      method: 'GET',
      path: '/orders/customers/:customerId/flags',
      pathParams: z.object({
        customerId: z.string().uuid(),
      }),
      responses: {
        200: CustomerFlagsResponseSchema,
      },
    },

    getUpcomingOrder: {
      method: 'GET',
      path: '/orders/customers/:customerId/upcoming',
      pathParams: z.object({
        customerId: z.string().uuid(),
      }),
      responses: {
        200: OrderResponseSchema,
        204: c.noBody(),
      },
    },

    updateCustomerFlags: {
      method: 'PATCH',
      path: '/orders/customers/:customerId/flags',
      body: OrderFlagsInputSchema,
      pathParams: z.object({
        customerId: z.string().uuid(),
      }),
      responses: {
        204: null,
      },
    },

    getPhoenixToggleOrders: {
      method: 'GET',
      path: '/orders/lookup/by-toggle',
      summary: 'Get Phoenix orders with phoenix_toggle enabled',
      description: 'Queries orders joined with order_flags where phoenix_toggle = true, filtered by make_date range and facility IDs. Used for reconciliation with Legacy.',
      query: GetPhoenixToggleOrdersQuerySchema,
      responses: {
        200: GetPhoenixToggleOrdersResponseSchema,
      },
    },

    getUpcomingOrdersForProductionDateAlert: {
      method: 'GET',
      path: '/orders/lookup/upcoming-with-dates',
      summary: 'Get upcoming Phoenix-toggle orders with dates/depot/state for production date cap alert',
      description: 'Returns non-cancelled orders with order_flags.phoenix_toggle = true in a make_date range, including the fields needed to compute whether BeCool production_date will be capped (gap > 7 days). Used by the productionDateCapAlert Temporal workflow. Excludes Laravel-managed orders, which have their own BeCool integration path.',
      query: GetUpcomingOrdersForProductionDateAlertQuerySchema,
      responses: {
        200: GetUpcomingOrdersForProductionDateAlertResponseSchema,
      },
    },

    addOrderSubscriptionExtra: {
      method: 'POST',
      path: '/orders/line-items/add-subscription-extras',
      body: AddOrderSubscriptionExtraInputSchema,
      responses: {
        200: OrderResponseSchema.array(),
      },
    },
    deleteOrderSubscriptionExtra: {
      method: 'POST',
      path: '/orders/line-items/delete-subscription-extras',
      body: DeleteOrderSubscriptionExtraInputSchema,
      responses: {
        200: OrderResponseSchema.array(),
      },
    },
    updateOrderSubscriptionExtra: {
      method: 'POST',
      path: '/orders/line-items/update-subscription-extras',
      body: UpdateOrderSubscriptionExtraInputSchema,
      responses: {
        200: OrderResponseSchema.array(),
      },
    },
    moveOrderSubscriptionExtra: {
      method: 'POST',
      path: '/orders/line-items/move-subscription-extras',
      body: MoveOrderSubscriptionExtraInputSchema,
      responses: {
        200: OrderResponseSchema.array(),
      },
    },
    createOrderDiscount: {
      method: 'POST',
      description: 'Create a new order discount',
      path: '/orders/:orderId/discounts',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      body: CreateOrderDiscountBodySchema,
      responses: {
        201: CreateOrderDiscountResponseSchema,
      },
    },
    createOrderReferrerReward: {
      method: 'POST',
      description: 'Create a referrer reward record, optionally linked to an order',
      path: '/orders/referrer-rewards',
      body: CreateOrderReferrerRewardBodySchema,
      responses: {
        201: CreateOrderReferrerRewardResponseSchema,
      },
    },

    updateOrderReferrerReward: {
      method: 'PATCH',
      description: 'Update a referrer reward record order link; rewardAppliedAt is set when orderId is set and cleared when orderId is null',
      path: '/orders/referrer-rewards/:referrerRewardId',
      pathParams: z.object({
        referrerRewardId: z.string().uuid(),
      }),
      body: UpdateOrderReferrerRewardBodySchema,
      responses: {
        200: OrderReferrerRewardRecordSchema,
        400: ErrorResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
      },
    },

    deleteOrderReferrerReward: {
      method: 'DELETE',
      description: 'Delete a referrer reward record that is not linked to an order',
      path: '/orders/referrer-rewards/:referrerRewardId',
      pathParams: z.object({
        referrerRewardId: z.string().uuid(),
      }),
      responses: {
        204: z.undefined(),
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
      },
    },
    migrateOrderDiscounts: {
      method: 'POST',
      description: 'Migrate an existing order discount from legacy system. This is used for backfilling discounts for existing orders after the new discount system is in place.',
      path: '/orders/discounts/migrate',
      body: z.object({
        orderDiscount: MigrateOrderDiscountSchema.array(),
        dryRun: z.boolean().optional().describe('If true, the API will validate the input and return the discounts that would be created, but will not actually create the discounts.'),
      }),
      responses: {
        201: CreateOrderDiscountResponseSchema.array(),
      },
    },
    verifyOrderDiscounts: {
      method: 'POST',
      description: 'Verify migrated order discounts against source data.',
      path: '/orders/discounts/verify',
      body: z.object({
        orderDiscount: MigrateOrderDiscountSchema.array(),
      }),
      responses: {
        200: VerifyOrderDiscountResponseSchema,
      },
    },
    deleteMigratedOrderDiscountsByUserId: {
      method: 'DELETE',
      description: 'Delete all migrated order discounts for a given user. Finds all orders for the userId, then removes all order discounts attached to those orders.',
      path: '/orders/discounts/migration/:userId',
      pathParams: z.object({
        userId: z.string().uuid(),
      }),
      body: z.object({
        dryRun: z.boolean().optional().default(false).describe('If true, the transaction is rolled back and no data is actually deleted. The response still shows what would have been deleted.'),
      }),
      responses: {
        200: DeleteMigratedOrderDiscountsResponseSchema,
      },
    },
    deleteAllOrderDiscounts: {
      method: 'DELETE',
      description: 'Delete all discounts for an order',
      path: '/orders/:orderId/discounts',
      pathParams: z.object({
        orderId: z.string().uuid(),
      }),
      responses: {
        204: z.undefined(),
      },
    },
    deleteOrderDiscount: {
      method: 'DELETE',
      description: 'Delete a specific discount against an order',
      path: '/orders/:orderId/discounts/:discountId',
      pathParams: z.object({
        orderId: z.string().uuid(),
        discountId: z.string().uuid(),
      }),
      responses: {
        204: z.undefined(),
      },
    },
    verifyOrders: {
      method: 'POST',
      description: 'Verify order correctness after subscription migration',
      path: '/orders/verify',
      body: z.object({
        userId: z.string().uuid(),
        orders: OrderInputSchema.array(),
      }),
      responses: {
        200: VerifyOrderResponseSchema,
      },
    },
    backfillProductType: {
      method: 'POST',
      description: 'Run a single batch of product_type backfill. Call repeatedly until phase=done.',
      path: '/orders/admin/backfill-product-type',
      body: BackfillProductTypeInputSchema,
      responses: {
        200: BackfillProductTypeOutputSchema,
      },
    },

    claimOutboxBatch: {
      method: 'POST',
      description: 'Atomically claim a batch of unprocessed outbox events: selects rows where claimed_at IS NULL AND succeeded_at IS NULL with FOR UPDATE SKIP LOCKED, stamps each with claimed_at=now() and a deterministic workflow_id, and returns the claimed rows. Race-safe under concurrent pollers, overlapping callers receive disjoint row sets. Pass `eventTypes` to claim only the types your dispatcher can process; anything else is left untouched for a later deploy. Used by the Netsuite outbox poller workflow.',
      path: '/outbox/claim-batch',
      body: ClaimOutboxBatchBodySchema,
      responses: {
        200: ClaimOutboxBatchResponseSchema,
      },
    },

    markOutboxProcessed: {
      method: 'PATCH',
      description: 'Mark an outbox row as processed: set succeeded_at and optionally write a result_payload (consumed by downstream rows via predecessor_id). Final callback from the dispatched child workflow.',
      path: '/outbox/:id/processed',
      pathParams: z.object({ id: z.string() }),
      body: MarkOutboxProcessedBodySchema,
      responses: {
        204: c.noBody(),
      },
    },

    getOutboxPredecessor: {
      method: 'GET',
      description: 'Fetch the predecessor row of an outbox row (via predecessor_id) so the dispatched child workflow can pick up the NetSuite ids and the post-rounding total earned by the previous step in the DAG. Returns 404 if the row has no predecessor (e.g. a head-of-chain order.paid SO row) or if either row does not exist.',
      path: '/outbox/:id/predecessor',
      pathParams: z.object({ id: z.string() }),
      responses: {
        200: OutboxPredecessorSchema,
        404: ErrorResponseSchema,
      },
    },

    getOutboxSupersedingRow: {
      method: 'GET',
      description: 'Report whether a row on the same order carrying a newer statement of its Sales Order has already succeeded, returning its id as `supersededBy` or null. Read by both branches that restate a Sales Order, before they do it: an additional charge raising it to the order\'s new total, and a partial reversal lowering it to what the customer is left paying. Either body snapshots the whole order and posts with ?replace=item, so a row whose turn has passed (e.g. a replay of a failed charge, run after a newer one already posted) must skip its own restatement rather than overwrite the newer one with an older snapshot. The two kinds are compared against each other, not just against their own kind. "Newer" means a later `chargedAt` (charge) or `refundedAt` (reversal) on the payload, i.e. when the snapshot was taken, never when the outbox row was written — a replay is written last but carries the original\'s older snapshot, so write order would answer this backwards. Rows whose consumer never touches the Sales Order, including `order.refunded:creditMemo`, always answer null. Returns 404 if the row does not exist.',
      path: '/outbox/:id/superseding',
      pathParams: z.object({ id: z.string() }),
      responses: {
        200: OutboxSupersedingRowSchema,
        404: ErrorResponseSchema,
      },
    },

    markOutboxFailed: {
      method: 'PATCH',
      description: 'Record a failed workflow run on an outbox row. retryable=true transitions to RETRYABLE_FAILED, resets claimed_at/workflow_id, and stamps available_at so the row becomes re-claimable after the dispatcher\'s backoff window. retryable=false transitions to FAILED (terminal — needs replay tooling or manual ops transition).',
      path: '/outbox/:id/failed',
      pathParams: z.object({ id: z.string() }),
      body: MarkOutboxFailedBodySchema,
      responses: {
        204: c.noBody(),
      },
    },
    createOrderComplaint: {
      method: 'POST',
      description: 'Create a new order complaint (and its associated products) for an order.',
      path: '/orders/:orderId/complaints',
      pathParams: z.object({ orderId: z.string().uuid() }),
      body: CreateOrderComplaintInputSchema,
      responses: {
        200: OrderComplaintSchema,
      },
    },
    migrateOrderComplaint: {
      method: 'POST',
      description: 'Migrate an existing order complaint from legacy system. This is used for backfilling complaints for existing orders after the new complaint system is in place.',
      path: '/orders/:orderId/complaints/migrate',
      pathParams: z.object({ orderId: z.string().uuid() }),
      body: MigrateOrderComplaintSchema,
      responses: {
        200: MigrateOrderComplaintSchema,
      },
    },
    getComplaintCategories: {
      method: 'GET',
      description: 'Return all (non-deleted) top-level order complaint categories, each with its subcategories nested under `subcategories`.',
      path: '/complaint-categories',
      responses: {
        200: GetComplaintCategoriesResponseSchema,
      },
    },
    getOrderComplaints: {
      method: 'GET',
      description: 'List order complaints filtered by userId and/or orderId. `userId` returns complaints across every order owned by that user; `orderId` returns complaints for a single order. At least one filter is required.',
      path: '/order-complaints',
      query: GetOrderComplaintsQuerySchema,
      responses: {
        200: GetOrderComplaintsResponseSchema,
      },
    },
    getOrderComplaintById: {
      method: 'GET',
      description: 'Fetch a single order complaint (with its products) by complaint id.',
      path: '/order-complaints/:complaintId',
      pathParams: z.object({ complaintId: z.string().uuid() }),
      responses: {
        200: OrderComplaintSchema,
        404: ErrorResponseSchema,
      },
    },
    deleteOrderComplaint: {
      method: 'DELETE',
      description: 'Soft-delete a single order complaint by id. Returns 404 if the complaint does not exist or has already been soft-deleted.',
      path: '/order-complaints/:complaintId',
      pathParams: z.object({ complaintId: z.string().uuid() }),
      responses: {
        204: z.undefined(),
        404: ErrorResponseSchema,
      },
    },
    updateOrderComplaint: {
      method: 'PATCH',
      description: 'Partially update an existing order complaint by id (and optionally replace its associated products). Returns 404 if the complaint does not exist.',
      path: '/order-complaints/:complaintId',
      pathParams: z.object({ complaintId: z.string().uuid() }),
      body: UpdateOrderComplaintInputSchema,
      responses: {
        200: OrderComplaintSchema,
        404: ErrorResponseSchema,
      },
    },
    getNetsuiteInternalId: {
      method: 'GET',
      description: 'Read-before-write idempotency check: return the netsuite_internal_ids row for an (orderId, resourceType), or 404 if Phoenix has not yet recorded a NetSuite id for that resource. Called by every NetSuite POST activity before it posts.',
      path: '/netsuite-internal-ids',
      query: GetNetsuiteInternalIdQuerySchema,
      responses: {
        200: NetsuiteInternalIdSchema,
        404: ErrorResponseSchema,
      },
    },

    listNetsuiteInternalIds: {
      method: 'GET',
      description: 'Return all netsuite_internal_ids rows for an order in one round-trip (e.g. the Sales Invoice activity needs the SO + Item Fulfilment ids; customer-care / ops debugging).',
      path: '/netsuite-internal-ids/order/:orderId',
      pathParams: z.object({ orderId: z.string().uuid() }),
      responses: {
        200: ListNetsuiteInternalIdsResponseSchema,
      },
    },

    recordDiscoveredNetsuiteInternalId: {
      method: 'POST',
      description: 'Record a netsuite_internal_ids row that has no producing outbox row: a discovered record (NetSuite auto-created it, phoenixExternalId null) or the customer-creation case (created/alreadyExists, no source outbox). UPSERTs on (orderId, resourceType). Returns 409 if the NetSuite id already maps to a different order for the same resource type.',
      path: '/netsuite-internal-ids/discovered',
      body: RecordDiscoveredNetsuiteInternalIdBodySchema,
      responses: {
        200: NetsuiteInternalIdSchema,
        409: ErrorResponseSchema,
      },
    },

    backfillOutboxForOrder: {
      method: 'POST',
      description: 'Admin/ops backfill: manufacture the NetSuite outbox rows an already-terminal order is missing, derived from its current payment and fulfilment status. Requires the order\'s netsuiteOutboxToggle to be ON (skips with toggle_off otherwise) — flip the cohort on first; the endpoint never writes the toggle. Inserts PENDING rows with canonical idempotency keys (ON CONFLICT DO NOTHING) and resolves predecessors by identity so a partial pre-existing chain links correctly; never replays a state transition or publishes to SNS. Created-PROCESSED free/duplicate orders with no billedDate fall back to the creation time as the Sales Order tranDate source. Re-running the same order is a no-op. dryRun reports the per-stage plan without inserting. Returns 404 if the order does not exist.',
      path: '/admin/orders/:orderId/outbox/backfill',
      pathParams: z.object({ orderId: z.string().uuid() }),
      body: BackfillOutboxBodySchema,
      responses: {
        200: BackfillOutboxResponseSchema,
        404: ErrorResponseSchema,
      },
    },
    replayOutboxRow: {
      method: 'POST',
      description: 'Admin/ops replay: write an outbox_replay audit row plus a fresh PENDING outbox row in one transaction. The source row is never mutated. The new row is picked up by the next claim tick. Only FAILED or BLOCKED rows are replayable. Returns 404 if the source row does not exist, 409 if the row is not in a replayable state (e.g. SUCCEEDED, or still in flight as PENDING/CLAIMED/PROCESSING/RETRYABLE_FAILED) or has already been replayed (idempotency_key collision).',
      path: '/admin/outbox/:id/replay',
      pathParams: z.object({ id: z.string() }),
      body: ReplayOutboxRowBodySchema,
      responses: {
        201: ReplayOutboxRowResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
      },
    },
    getNetsuiteSyncSummary: {
      method: 'GET',
      description: 'Counts behind the daily NetSuite sync digest, read from the order DB only — no NetSuite call, so every "synced" figure means "Phoenix recorded a NetSuite id", never "the record exists in NetSuite". Two cohorts: `paidToday` covers orders whose billed_date falls on the requested calendar day in the requested timezone (Sales Orders and Customer Deposits are due at paid, and free orders are reported separately because they expect a Sales Order and no deposit); `lifecycle` covers toggled orders that reached PALLETISED (by make_date) or DELIVERED (by delivery_date) on the requested day or the day before — day-scoped so the cohort never grows with history, with the extra day so a miss appearing after the last evening check is still caught — and chain-consistent (only orders whose predecessor record is already synced count as due) so orders palletised before the outbox existed are out of scope rather than permanently missing. Also returns the all-days FAILED/BLOCKED/stuck backlog, the attempt_failed leak check, and a capped needsAttention list alongside an exact total.',
      path: '/admin/netsuite-sync/daily-summary',
      query: NetsuiteSyncSummaryQuerySchema,
      responses: {
        200: NetsuiteSyncSummaryResponseSchema,
      },
    },
    deletePendingOrdersByUserId: {
      method: 'DELETE',
      description: 'Delete pending orders by userId, this is only used by migrations for a paused subscriptions',
      path: '/orders/user/:userId',
      pathParams: z.object({ userId: z.string().uuid() }),
      responses: {
        200: DeletePendingOrderByUserIdResponseSchema,
      }
    }
  },
  options,
)
